import { useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { Navigate, useParams, Link as RouterLink } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { usePrerenderReady } from '../hooks/usePrerenderReady'
import { SurahIndexList } from '../components/SurahIndexList'
import {
  SITE_ORIGIN,
  buildSurahIndexUrl,
  buildSurahIndexTitle,
  buildSurahIndexDescription,
} from '../lib/seo'
import type { SupportedLang } from '../lib/seo'

/**
 * `/<lang>/surah` — the per-language surah index, and the site's crawl hub.
 *
 * Purpose is structural, not decorative: it gives the 228 prerendered surah
 * pages an inbound internal link from one shallow, canonical URL, and it is
 * linked back from every surah page. Before it existed those pages were
 * reachable only via sitemap.xml, and Search Console parked 185 of them as
 * "Discovered - currently not indexed".
 *
 * Copy comes from `lib/seo.ts` builders keyed on the URL's `:lang` param, not
 * from i18next: this page is prerendered per language, and the snapshot must
 * be deterministic rather than depending on what the headless browser's
 * language detector happens to pick.
 */
export default function SurahIndexPage() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()
  const langValid = lang === 'en' || lang === 'fr'

  // Drive the app language from the URL's `:lang` segment, same as
  // SurahReaderPage: the i18n detector never reads the URL, so without this
  // /fr/surah renders French page copy inside English chrome and the document
  // keeps `<html lang="en">`, contradicting its own hreflang. The change flows
  // through i18n's `languageChanged` → `document.documentElement.lang` sync.
  useEffect(() => {
    if (langValid && i18n.language !== lang) {
      void i18n.changeLanguage(lang)
    }
  }, [lang, langValid, i18n])

  // Nothing on this page waits on the network — the surah table is static —
  // so the snapshot is ready on first paint.
  usePrerenderReady(true)

  if (!langValid) {
    return <Navigate to="/" replace />
  }

  const typedLang = lang as SupportedLang
  const title = buildSurahIndexTitle(typedLang)
  const description = buildSurahIndexDescription(typedLang)
  const canonical = buildSurahIndexUrl(typedLang)
  const altEn = buildSurahIndexUrl('en')
  const altFr = buildSurahIndexUrl('fr')

  const heading = typedLang === 'fr' ? 'Les 114 sourates du Coran' : 'The 114 Surahs of the Quran'
  const intro =
    typedLang === 'fr'
      ? 'Choisissez une sourate pour la lire en arabe avec la traduction française, verset par verset.'
      : 'Pick a surah to read it in Arabic with the English translation, verse by verse.'
  const readerCta =
    typedLang === 'fr' ? 'Ouvrir le lecteur interactif →' : 'Open the interactive reader →'

  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: heading, item: canonical },
    ],
  }

  return (
    <Box
      component="main"
      sx={{ px: { xs: 2, sm: 0 }, width: '100%', maxWidth: 960, mx: 'auto' }}
    >
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <link rel="alternate" hrefLang="en" href={altEn} />
        <link rel="alternate" hrefLang="fr" href={altFr} />
        <link rel="alternate" hrefLang="x-default" href={altEn} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Qlearn" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={`${SITE_ORIGIN}/pwa-512.png`} />
        <meta property="og:locale" content={typedLang === 'fr' ? 'fr_FR' : 'en_US'} />
        <meta
          property="og:locale:alternate"
          content={typedLang === 'fr' ? 'en_US' : 'fr_FR'}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={`${SITE_ORIGIN}/pwa-512.png`} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbs)}</script>
      </Helmet>

      <Box component="header" sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {heading}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {intro}
        </Typography>
      </Box>

      <SurahIndexList lang={typedLang} />

      <Box sx={{ mt: 3 }}>
        <Typography
          component={RouterLink}
          to="/reader"
          variant="body2"
          sx={{ color: 'primary.main', textDecoration: 'none', fontWeight: 600 }}
        >
          {readerCta}
        </Typography>
      </Box>
    </Box>
  )
}
